/* Outboard Cover Finder — Brand → Horsepower → engine confirm → recommended
   covers with live, market-scoped pricing. Reads
   assets/outboard-cover-finder-data.json (brands, engines keyed by brand with
   hp/year/covers[{style, sku, shaft, handle}], plus a universal drawstring
   range), fetches each cover product live by handle (Storefront API @inContext)
   and matches the exact variant by SKU so prices and variant ids are always
   correct for the current store/market. The engine confirm photo is resolved
   from an "engine" metaobject by handle (slug of brand-root-hp). Initialises
   every .ocf instance on the page. No dependencies. */
(function () {
  "use strict";

  // After an add, open the theme's cart drawer (it rebuilds the header count and
  // drawer contents itself) by triggering the header cart icon's own handler.
  // Without a drawer (cart page mode), just refresh the count and contents.
  function refreshCart() {
    var opener = document.querySelector(".openCartDrawer");
    if (opener && window.jQuery) { opener.click(); return; }
    fetch("/cart.js", { headers: { Accept: "application/json" } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (window.buildCart && window.jQuery) { window.buildCart(cart, false); return; }
        document.querySelectorAll("[data-cart-count]").forEach(function (n) {
          n.textContent = cart.item_count < 100 ? cart.item_count : "";
          n.style.display = cart.item_count > 0 ? "" : "none";
        });
      })
      .catch(function () {});
  }

  function init(root) {
    if (root.dataset.ocfReady) return;
    root.dataset.ocfReady = "1";

    var market = window.OS_MARKET || {};
    var token = root.dataset.token || "";
    var country = String(market.country || root.dataset.country || "AU")
      .toUpperCase().replace(/[^A-Z]/g, "").slice(0, 2) || "AU";
    var rootUrl = (market.root || root.dataset.rootUrl || "/").replace(/\/+$/, "") + "/";
    var apiVersion = root.dataset.apiVersion || "2024-10";
    var dataSrc = root.dataset.src;
    // Shared "previous generation outboards" image, shown when an engine has no
    // individual confirm photo (older models share one repeatable image).
    var olderImg = root.dataset.olderImg || "";
    // Line drawing of each cover type in use, shown beside the result card.
    // Universal is the same product shape as a full cover, so it reuses that one.
    var DIAGRAMS = {
      full: root.dataset.diagramFull || "",
      vented: root.dataset.diagramVented || "",
      cowling: root.dataset.diagramCowling || "",
      universal: root.dataset.diagramFull || "",
      set: root.dataset.diagramFull || "",
    };
    // Diagram behind the "How to measure shaft length" button.
    var shaftDiagram = root.dataset.shaftDiagram || "";
    var enquireUrl = rootUrl + "pages/contact";
    var endpoint = rootUrl.replace(/\/+$/, "") + "/api/" + apiVersion + "/graphql.json";

    var brandSel = root.querySelector('[data-role="brand"]');
    var hpSel = root.querySelector('[data-role="hp"]');
    var engineBlock = root.querySelector('[data-role="engine"]');
    var tilesWrap = root.querySelector('[data-role="engine-tiles"]');
    var results = root.querySelector('[data-role="results"]');
    var emptyNote = root.querySelector('[data-role="empty"]');
    var prompt_ = root.querySelector(".ocf__engine-prompt");
    var chosenBar = null; // summary + "choose a different engine" control

    var VIDEOS = {};
    var videosEl = root.querySelector('[data-role="ocf-videos"]');
    if (videosEl) { try { VIDEOS = JSON.parse(videosEl.textContent) || {}; } catch (e) { VIDEOS = {}; } }
    var DESCRIPTIONS = {};
    var descsEl = root.querySelector('[data-role="ocf-descriptions"]');
    if (descsEl) { try { DESCRIPTIONS = JSON.parse(descsEl.textContent) || {}; } catch (e) { DESCRIPTIONS = {}; } }

    // Liquid's `t` filter HTML-escapes every translation whose key doesn't end
    // in _html, so an apostrophe arrives here as &#39;. These strings are then
    // written to the DOM as text, where nothing decodes them and the entity
    // shows up verbatim in the copy. Undo the escaping the filter applies.
    var ENTITIES = { "&amp;": "&", "&lt;": "<", "&gt;": ">", "&quot;": '"', "&#39;": "'", "&#x27;": "'" };
    function decodeEntities(value) {
      if (typeof value === "string") {
        return value.replace(/&(?:amp|lt|gt|quot|#39|#x27);/gi, function (m) {
          return ENTITIES[m.toLowerCase()] || m;
        });
      }
      if (value && typeof value === "object") {
        Object.keys(value).forEach(function (k) { value[k] = decodeEntities(value[k]); });
      }
      return value;
    }
    // Visible copy, from locales/*.json via the section (see ocf-i18n). Every
    // lookup falls back to the English literal, so a missing or malformed key
    // degrades to the old hard-coded wording rather than blanking the UI.
    var I18N = {};
    var i18nEl = root.querySelector('[data-role="ocf-i18n"]');
    if (i18nEl) { try { I18N = decodeEntities(JSON.parse(i18nEl.textContent) || {}); } catch (e) { I18N = {}; } }
    function t(key, fallback) {
      var v = I18N[key];
      return (typeof v === "string" && v !== "") ? v : fallback;
    }
    // Placeholders differ by source: the locale files write Shopify's
    // {{ name }} form, the English fallbacks below use %s, and the Translate &
    // Adapt overrides live on the store carry %s too. Fill whichever form the
    // active string actually holds, so no source renders its marker raw.
    function fill(str, name, value) {
      return String(str).replace(
        new RegExp("%s|\\{\\{\\s*" + name + "\\s*\\}\\}"),
        function () { return value; }
      );
    }

    // Result cards are grouped by cover style and shown in this order.
    var STYLE_ORDER = ["Vented / Running Cover", "Full Storage Cover", "Cowling Storage Cover"];

    // Combo ("Cover Set") products pair a vented and a full cover in one buy.
    // They are one product per brand, and their variant SKUs are the full
    // cover's SKU with a C on the end (E00-20 -> E00-20C). Only the bigger
    // engines have a set, so the card is offered strictly when a matching
    // variant actually exists in the store — never inferred from the pattern.
    var COMBO_STYLE = "Cover Set (Vented and Full)";
    var PSEUDO_ROOT = "previous-generation";

    // Explainer copy, verbatim from the "Non-Specific Model & Combo" tab of the
    // 250826 SAP workbook. Kept as data so it can be re-synced from that tab.
    var NON_SPECIFIC_HELP = {
      label: t("help_ns_label", "What is Non-Specific Model?"),
      paras: [
        t("help_ns_p1", "Select Non-Specific Model if your outboard motor does not have a specific model name, series or distinguishing feature shown in the available options, or if you are unsure of the exact model. If you know the brand, horsepower (HP) and leg/shaft length of your motor but cannot identify a specific designation or model variant, select Non-Specific Model to find the most suitable cover."),
        t("help_ns_p2", "If you are unsure of your motor's leg/shaft length, refer to our How to Measure Your Outboard Motor Leg/Shaft Length guide before making your selection."),
      ],
    };
    var UNIVERSAL_WHY_HELP = {
      label: t("help_universal_label", "Why choose a Universal Outboard Motor Cover?"),
      paras: [
        t("help_universal_p1", "Oceansouth Universal Outboard Motor Covers are a fantastic option when your particular outboard isn't available in our Custom Cover range. Our Custom Covers are designed specifically for the individual outboard models listed on our website, with actual photos showing the cover fitted to the corresponding motor. If you can't find your exact outboard model amongst these Custom Covers, our Universal range provides an excellent alternative, with a wide selection of sizes designed to accommodate outboards of different horsepower and shaft lengths."),
        t("help_universal_p2", "Available in two colours, Oceansouth Universal Covers provide dependable protection for your outboard when stored or trailering. Simply select your outboard's brand, horsepower, model (where applicable) and shaft/leg length to find the most suitable cover. If your outboard does not have a specific model designation listed, or you are unsure of the exact model, select Non-Specific Model. Each Universal Cover also features a distinctive high-visibility red tail around the propeller area, helping make the covered motor more noticeable when towing or parked."),
      ],
    };
    var UNIVERSAL_COWLING_HELP = {
      label: t("help_universal_cowling_label", "What is a Universal Cowling Storage Cover?"),
      paras: [
        t("help_universal_cowling_p1", "Oceansouth Universal Cowling Storage Covers are a fantastic option when your particular outboard isn't available in our Custom Cover range. Oceansouth Universal Cowling Storage Covers provide a simple and effective way to protect the cowling of your outboard motor when it is not in use. If your particular outboard isn't listed in our Custom Cover range, an Oceansouth Universal Cowling Storage Cover is an excellent alternative."),
        t("help_universal_cowling_p2", "Designed to fit a wide range of outboard motor shapes and sizes, our Universal Cowling Storage Covers offer reliable protection from sun, dust, dirt and the elements, helping to keep your motor cowling looking its best."),
      ],
    };
    var COMBO_HELP = {
      label: t("help_combo_label", "Why choose the Cover Set?"),
      paras: [
        t("help_combo_p1", "Get the best of both worlds while saving money with the Oceansouth Vented/Running and Full Storage Cover Combo Set. Purchasing both covers together gives you a generous discount compared with buying each cover separately, making the combo the best-value option for complete year-round protection of your outboard."),
        t("help_combo_p2", "The Vented/Running Cover is designed to remain fitted while the outboard is being used, protecting the cowling from sun, spray and everyday wear. When the boat is stored or being trailered, simply fit the Full Storage Cover straight over the top — there's no need to remove the Vented/Running Cover first. With both covers working together, your outboard stays protected whether you're on the water, on the road or in storage."),
      ],
    };
    var COMBO_HANDLES = {
      Evinrude: "cover-set-vented-and-full-for-evinrude",
      Honda: "cover-set-vented-and-full-for-honda",
      Mercury: "cover-set-vented-and-full-for-mercury",
      Suzuki: "cover-set-suzuki",
      Tohatsu: "cover-set-vented-and-full-for-tohatsu",
      Yamaha: "cover-set-vented-and-full-for-yamaha",
    };
    function comboSku(sku) { return String(sku).replace(/\s+/g, "") + "C"; }

    // Some SKU roots share a horsepower, so "40 HP" alone can't tell two engine
    // cards apart — Mercury 40 exists as both M20 (4-cylinder) and M25
    // (3-cylinder), and the M000 range is the Verado line that overlaps M00/M02
    // at 175-300 HP. These labels are what make the confirm step decidable.
    //   name: replaces the plain HP line, e.g. "Verado 225HP"
    //   note: extra descriptor line under it
    //   years: overrides the year line when the SAP export is wrong/stale
    // Keyed by SKU root, which is unique per brand except "MA 075" (the
    // universal range, shared by all five brands) — don't add an entry for it.
    var ROOT_LABELS = {
      M20: { note: "4 Stroke 4 Cyl" },
      M25: { note: "4 Stroke 3 Cyl" },
      M000: { name: "Verado" },
      Y25: { years: "2010–2025" },
      // Yamaha 350HP has two generations with different covers.
      Y00: { note: "V8 5.3L" },
      Y05A: { note: "V6 4.3L" },
    };
    function engineTitle(engine, hp) {
      var lab = ROOT_LABELS[engine.root];
      return lab && lab.name ? lab.name + " " + hp + "HP" : hp + " HP";
    }
    function engineNote(engine) {
      var lab = ROOT_LABELS[engine.root];
      return (lab && lab.note) || "";
    }
    // Short key per cover type — drives the badge colour and card accent.
    function typeKey(style) {
      var s = String(style || "").toLowerCase();
      // The set must be tested first: its name ("Cover Set (Vented and Full)")
      // contains the other types' words, so any earlier match would swallow it.
      if (s.indexOf("cover set") >= 0 || s.indexOf("set (") >= 0) return "set";
      if (s.indexOf("vented") >= 0 || s.indexOf("running") >= 0) return "vented";
      if (s.indexOf("cowling") >= 0) return "cowling";
      if (s.indexOf("universal") >= 0) return "universal";
      return "full";
    }

    var DATA = null;
    var productCache = {};   // handle -> Promise<product|null>
    var engineImgCache = {}; // metaobject handle -> Promise<url|null>
    var renderToken = 0;    // guards async combo lookups against stale selections

    // ---------- helpers ----------
    function el(tag, cls, text) {
      var e = document.createElement(tag);
      if (cls) e.className = cls;
      if (text != null) e.textContent = text;
      return e;
    }
    function apiUrl() { return endpoint; }
    function numericId(gid) { return String(gid || "").split("/").pop(); }
    function productUrl(handle, varId) {
      var u = rootUrl + "products/" + handle;
      return varId ? u + "?variant=" + varId : u;
    }
    function slug(s) {
      return String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
    }
    function formatPrice(money) {
      if (!money) return "";
      try {
        return new Intl.NumberFormat(undefined, {
          style: "currency", currency: money.currencyCode,
        }).format(parseFloat(money.amount));
      } catch (e) {
        return money.currencyCode + " " + money.amount;
      }
    }

    // ---------- Storefront GraphQL ----------
    function fetchProduct(handle) {
      if (productCache[handle]) return productCache[handle];
      var query =
        "query @inContext(country: " + country + ") { product(handle: " +
        JSON.stringify(handle) + ") { title featuredImage { url(transform:{maxWidth:640}) altText } " +
        "variants(first: 100) { nodes { sku title availableForSale id " +
        "price { amount currencyCode } image { url(transform:{maxWidth:640}) altText } " +
        "selectedOptions { name value } } } } }";
      var p = fetch(apiUrl(), {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Shopify-Storefront-Access-Token": token },
        body: JSON.stringify({ query: query }),
      })
        .then(function (r) { return r.json(); })
        .then(function (j) { return (j && j.data && j.data.product) || null; })
        .catch(function () { return null; });
      productCache[handle] = p;
      return p;
    }

    // Engine confirm photo from the "engine" metaobject. Never throws.
    function fetchEngineImage(handle) {
      if (engineImgCache[handle]) return engineImgCache[handle];
      var query =
        'query($h:String!){ metaobject(handle:{type:"engine", handle:$h}){ ' +
        "field(key:\"image\"){ reference{ ... on MediaImage{ image{ url } } } } } }";
      var p = fetch(apiUrl(), {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Shopify-Storefront-Access-Token": token },
        body: JSON.stringify({ query: query, variables: { h: handle } }),
      })
        .then(function (r) { return r.json(); })
        .then(function (j) {
          try {
            var mo = j && j.data && j.data.metaobject;
            var url = mo && mo.field && mo.field.reference && mo.field.reference.image && mo.field.reference.image.url;
            return url || null;
          } catch (e) { return null; }
        })
        .catch(function () { return null; });
      engineImgCache[handle] = p;
      return p;
    }

    // Batch: fetch every engine image for a selection in ONE query, at a
    // CDN-resized size, so the confirm tiles fill together (no per-tile
    // round-trips / staggered pop-in) and download light. Primes engineImgCache.
    function fetchEngineImages(handles) {
      var need = [];
      handles.forEach(function (h) { if (h && !engineImgCache[h]) need.push(h); });
      if (!need.length) return;
      var parts = need.map(function (h, i) {
        return "e" + i + ':metaobject(handle:{type:"engine",handle:' + JSON.stringify(h) +
          '}){field(key:"image"){reference{...on MediaImage{image{url(transform:{maxWidth:900,maxHeight:900})}}}}}';
      });
      var pAll = fetch(apiUrl(), {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Shopify-Storefront-Access-Token": token },
        body: JSON.stringify({ query: "query{" + parts.join(" ") + "}" }),
      })
        .then(function (r) { return r.json(); })
        .then(function (j) {
          var data = (j && j.data) || {}, map = {};
          need.forEach(function (h, i) {
            var mo = data["e" + i];
            map[h] = (mo && mo.field && mo.field.reference && mo.field.reference.image &&
              mo.field.reference.image.url) || null;
          });
          return map;
        })
        .catch(function () { var map = {}; need.forEach(function (h) { map[h] = null; }); return map; });
      need.forEach(function (h) { engineImgCache[h] = pAll.then(function (map) { return map[h]; }); });
    }

    // ---------- colour swatch helpers (mirrors BCF) ----------
    function colorHex(name) {
      var n = (name || "").toLowerCase();
      if (n.indexOf("black") >= 0) return "#1c1f24";
      if (n.indexOf("grey") >= 0 || n.indexOf("gray") >= 0) return "#9aa3ad";
      if (n.indexOf("blue") >= 0) return "#1f4368";
      if (n.indexOf("white") >= 0) return "#eef0f2";
      if (n.indexOf("green") >= 0) return "#3a5a40";
      return "#8a93a3";
    }
    function isColorOpt(o) { return /colou?r/i.test(o.name); }
    function nonColorSig(v) {
      var so = v.selectedOptions || [], parts = [], i;
      for (i = 0; i < so.length; i++) { if (!isColorOpt(so[i])) parts.push(so[i].name + "=" + so[i].value); }
      return parts.sort().join("|");
    }
    function colorSiblings(variants, matched) {
      var mo = matched.selectedOptions || [], hasColor = false, i;
      for (i = 0; i < mo.length; i++) { if (isColorOpt(mo[i])) hasColor = true; }
      if (!hasColor) return null;
      var target = nonColorSig(matched), out = [];
      for (i = 0; i < variants.length; i++) {
        if (nonColorSig(variants[i]) !== target) continue;
        var cv = "", so = variants[i].selectedOptions || [], j;
        for (j = 0; j < so.length; j++) { if (isColorOpt(so[j])) cv = so[j].value; }
        if (cv) out.push({ name: cv, variant: variants[i] });
      }
      return out;
    }

    function matchVariant(variants, sku) {
      var want = String(sku || "").trim().toUpperCase(), i;
      for (i = 0; i < variants.length; i++) {
        if (variants[i].sku && variants[i].sku.trim().toUpperCase() === want) return variants[i];
      }
      return null;
    }

    // ---------- cover / shaft labels ----------
    function shaftLabel(shaft) {
      // shafts are always single numbers now — combined "15 or 20" values
      // were removed from the data (build script hard-errors on them)
      if (shaft == null || shaft === "") return "";
      return shaft + '"';
    }
    function optionLabel(cover) {
      if (cover.optionLabel) return cover.optionLabel;
      var s = shaftLabel(cover.shaft);
      if (s) return s;
      var parts = String(cover.sku || "").trim().split("-");
      return parts[parts.length - 1] || t("option", "Option");
    }
    function sizeText(cover) {
      if (cover.label) return cover.label;
      var s = shaftLabel(cover.shaft);
      return s ? t("shaft_length", "Shaft length") + ": " + s : "";
    }

    // ---------- info bubble (per card) ----------
    function wireInfo(btn, pop) {
      btn.addEventListener("click", function (e) {
        e.preventDefault(); e.stopPropagation();
        var open = !pop.classList.contains("is-open");
        pop.classList.toggle("is-open", open);
        btn.setAttribute("aria-expanded", String(open));
      });
      document.addEventListener("click", function (e) {
        if (pop.classList.contains("is-open") && !pop.contains(e.target) && e.target !== btn && !btn.contains(e.target)) {
          pop.classList.remove("is-open"); btn.setAttribute("aria-expanded", "false");
        }
      });
      document.addEventListener("keydown", function (e) {
        if (e.key === "Escape") { pop.classList.remove("is-open"); btn.setAttribute("aria-expanded", "false"); }
      });
    }
    // "How to measure shaft length" — same affordance as the video button, but
    // the payload is a diagram. Sits with the shaft dropdown, which is the
    // moment the shopper has to know their measurement.
    function buildShaftHelp() {
      if (!shaftDiagram) return null;
      var wrap = el("div");
      wrap.style.position = "relative";
      wrap.style.marginTop = "6px";
      var label = t("how_to_measure", "How to measure shaft length");
      var btn = el("button", "ocf__videobtn");
      btn.type = "button";
      btn.setAttribute("aria-expanded", "false");
      var icon = document.createElement("span");
      icon.className = "ocf__videobtn-icon";
      icon.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M12 3v18"/><path d="M8 6l4-3 4 3"/><path d="M8 18l4 3 4-3"/></svg>';
      btn.appendChild(icon);
      btn.appendChild(el("span", null, label));

      var pop = el("div", "ocf__info-pop");
      pop.setAttribute("role", "dialog");
      pop.setAttribute("aria-label", label);
      pop.appendChild(el("strong", "ocf__info-title", label));
      var fig = el("div", "ocf__shaftdiagram");
      var img = new Image();
      img.src = shaftDiagram;
      img.loading = "lazy";
      img.alt = t("shaft_diagram_alt", "Measuring outboard shaft length from the transom bracket to the cavitation plate");
      fig.appendChild(img);
      pop.appendChild(fig);

      wrap.appendChild(btn);
      wrap.appendChild(pop);
      wireInfo(btn, pop);
      return wrap;
    }

    // Same affordance as the video and shaft-diagram buttons, but the payload is
    // prose — used for the two explainers OS supplies in the workbook.
    function buildTextHelp(spec) {
      var wrap = el("div", "ocf__texthelp");
      wrap.style.position = "relative";
      var btn = el("button", "ocf__videobtn");
      btn.type = "button";
      btn.setAttribute("aria-expanded", "false");
      var icon = document.createElement("span");
      icon.className = "ocf__videobtn-icon";
      icon.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>';
      btn.appendChild(icon);
      btn.appendChild(el("span", null, spec.label));

      var pop = el("div", "ocf__info-pop");
      pop.setAttribute("role", "dialog");
      pop.setAttribute("aria-label", spec.label);
      pop.appendChild(el("strong", "ocf__info-title", spec.label));
      var bodyEl = el("div", "ocf__info-body");
      spec.paras.forEach(function (p) { bodyEl.appendChild(el("p", null, p)); });
      pop.appendChild(bodyEl);

      wrap.appendChild(btn);
      wrap.appendChild(pop);
      wireInfo(btn, pop);
      return wrap;
    }

    // Friendly cover-type name for the button label.
    function friendlyStyle(style) {
      if (/vented|running/i.test(style)) return t("style_vented", "Vented Cover");
      if (/cowling/i.test(style)) return t("style_cowling", "Cowling Cover");
      if (/full/i.test(style)) return t("style_full", "Full Storage Cover");
      if (/cover set|set \(/i.test(style)) return t("style_set", "Cover Set");
      if (/universal/i.test(style)) return t("style_universal", "Universal Cover");
      return style;
    }
    // Accept any YouTube URL (watch / youtu.be / embed) and return an embeddable one.
    function ytEmbed(url) {
      if (!url) return url;
      var m = String(url).match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{6,})/);
      return m ? "https://www.youtube.com/embed/" + m[1] : url;
    }
    // Explainer modal — a native <dialog> portalled to <body> so it sits in
    // the top layer. This theme's AOS/parallax leave transforms on section
    // ancestors that break position:fixed overlays (and the header chrome
    // peaks at z-index 99999), so an in-card overlay is not an option.
    var infoModal = null;
    function ensureInfoModal() {
      if (infoModal) return infoModal;
      var d = document.createElement("dialog");
      d.className = "ocf-modal";
      var inner = el("div", "ocf-modal__body");
      var head = el("div", "ocf-modal__head");
      head.appendChild(el("strong", "ocf-modal__title"));
      var close = el("button", "ocf-modal__close", "×");
      close.type = "button";
      close.setAttribute("aria-label", t("close", "Close"));
      head.appendChild(close);
      inner.appendChild(head);
      inner.appendChild(el("p", "ocf-modal__desc"));
      inner.appendChild(el("div", "ocf-modal__video"));
      d.appendChild(inner);
      close.addEventListener("click", function () { d.close(); });
      // dialog is the click target only on the backdrop (content sits in inner)
      d.addEventListener("click", function (e) { if (e.target === d) d.close(); });
      // closing must stop playback — removing the iframe is the only reliable way
      d.addEventListener("close", function () {
        d.querySelector(".ocf-modal__video").innerHTML = "";
      });
      document.body.appendChild(d);
      infoModal = d;
      return d;
    }
    function openInfoModal(style) {
      var d = ensureInfoModal();
      var q = fill(t("what_is_a", "What is a %s?"), "style", friendlyStyle(style));
      d.setAttribute("aria-label", q);
      d.querySelector(".ocf-modal__title").textContent = q;
      var desc = d.querySelector(".ocf-modal__desc");
      desc.textContent = DESCRIPTIONS[style] || "";
      desc.hidden = !DESCRIPTIONS[style];
      var vwrap = d.querySelector(".ocf-modal__video");
      vwrap.innerHTML = "";
      var url = ytEmbed(VIDEOS[style]);
      if (url) {
        var f = document.createElement("iframe");
        f.src = url + (url.indexOf("?") >= 0 ? "&" : "?") + "autoplay=1";
        f.setAttribute("allow", "autoplay; encrypted-media; fullscreen");
        f.setAttribute("allowfullscreen", "");
        f.title = q;
        vwrap.appendChild(f);
      } else {
        vwrap.appendChild(el("div", "ocf-modal__missing", t("video_coming_soon", "Video coming soon")));
      }
      if (typeof d.showModal === "function") d.showModal();
      else d.setAttribute("open", ""); // pre-dialog browsers: non-modal fallback
    }

    function buildInfo(style) {
      var wrap = el("div");
      wrap.style.marginTop = "6px";
      var q = fill(t("what_is_a", "What is a %s?"), "style", friendlyStyle(style));
      var btn = el("button", "ocf__videobtn");
      btn.type = "button";
      var icon = document.createElement("span");
      icon.className = "ocf__videobtn-icon";
      icon.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="12" cy="12" r="10"/><polygon points="10 8 16 12 10 16 10 8" fill="currentColor" stroke="none"/></svg>';
      btn.appendChild(icon);
      btn.appendChild(el("span", null, q));
      btn.addEventListener("click", function () { openInfoModal(style); });
      wrap.appendChild(btn);
      return wrap;
    }

    // ---------- result card ----------
    // covers: array of covers that share a style (or a single universal cover).
    // opts: { style, eyebrow, info }
    function buildCard(covers, opts) {
      opts = opts || {};
      // Dedupe selector options. Two data quirks produce doubles:
      // (1) typo'd SKU twins from the source sheet ("Y40 -S" unresolved vs
      //     "Y40-S" resolved) — keep the resolved one;
      // (2) size-run variants sharing shaft AND landing page (MA 075-6..-9,
      //     all 25") — one option is enough, the PDP handles exact variants.
      // Covers with the same label but different products stay separate.
      function normSku(c) { return String(c.sku || "").replace(/\s+/g, ""); }
      // Key includes the shaft: the same universal SKU legitimately appears
      // at two leg lengths (MA 075-2 fits 15" AND 20") and both must stay
      // selectable — a bare-SKU key collapsed them to one option.
      function dedupeKey(c) { return normSku(c) + "|" + (c.shaft == null ? "" : c.shaft); }
      var bySku = {};
      covers.forEach(function (c) {
        var k = dedupeKey(c);
        if (!bySku[k] || (bySku[k].unresolved && !c.unresolved)) bySku[k] = c;
      });
      var uniq = [], seenLabel = {};
      covers.forEach(function (c) {
        if (bySku[dedupeKey(c)] !== c) return;
        var lk = optionLabel(c) + "|" + (c.handle || "");
        if (seenLabel[lk]) return;
        seenLabel[lk] = true;
        uniq.push(c);
      });
      covers = uniq;
      // Shaft-length selector only. A dropdown of SKU suffixes ('V', 'S') is
      // noise — those covers come in one size, so the style card already says
      // everything the shopper needs.
      // Options render for covers that carry a shaft OR an explicit
      // optionLabel (cowling universal sizes) — bare suffix-SKU twins
      // ('V','S') still stay out of the picker.
      var shaftCovers = covers.filter(function (c) { return (c.shaft != null && c.shaft !== "") || c.optionLabel; });
      var hasShaftPicker = shaftCovers.length > 1;
      // Label-based options (cowling universal sizes) aren't shaft lengths —
      // the picker heading and the measuring helper would mislead there.
      var labelBased = hasShaftPicker && shaftCovers.every(function (c) { return c.shaft == null || c.shaft === ""; });
      var isMulti = hasShaftPicker; // dropdown present → don't also show the static "Shaft length: X"
      var firstCover = hasShaftPicker ? shaftCovers[0] : covers[0];
      if (opts.preselect) {
        for (var pi = 0; pi < (hasShaftPicker ? shaftCovers : covers).length; pi++) {
          var pc = (hasShaftPicker ? shaftCovers : covers)[pi];
          if (pc.sku === opts.preselect.sku &&
              (opts.preselect.shaft == null || pc.shaft === opts.preselect.shaft)) {
            firstCover = pc; break;
          }
        }
      }
      var card = el("div", "ocf__card ocf__card--loading");
      var media = el("div", "ocf__card-media");
      var body = el("div", "ocf__card-body");

      // The cover type is the choice the shopper is actually making, so it
      // carries a colour of its own and leads the card.
      card.dataset.type = typeKey(opts.style);
      // friendlyStyle(), not opts.style — the raw style is a DATA key ("Vented /
      // Running Cover") and must never reach the screen untranslated.
      var eyebrow = el("p", "ocf__typebadge", opts.eyebrow || friendlyStyle(opts.style) || "");
      var title = el("h3", "ocf__card-title", t("outboard_cover", "Outboard cover"));
      body.appendChild(eyebrow);
      body.appendChild(title);

      if (hasShaftPicker) {
        var field = el("div", "ocf__field");
        var shaftLabelId = "ocf-shaft-" + (opts.style || "").replace(/\W+/g, "") + "-" + Math.round(performance.now() * 1000);
        var lab = el("label", "ocf__label", labelBased ? t("size", "Size") : t("shaft_length", "Shaft length"));
        lab.id = shaftLabelId;
        field.appendChild(lab);
        // Buttons rather than a dropdown: every length stays visible, so the
        // shopper can see at a glance which sizes this cover comes in.
        var opts_ = el("div", "ocf__shaftopts");
        opts_.setAttribute("role", "group");
        opts_.setAttribute("aria-labelledby", shaftLabelId);
        var btns = [];
        shaftCovers.forEach(function (c) {
          var b = el("button", "ocf__shaftopt", optionLabel(c));
          b.type = "button";
          b.setAttribute("aria-pressed", String(c === firstCover));
          if (c === firstCover) b.classList.add("is-selected");
          b.addEventListener("click", function () {
            btns.forEach(function (other) {
              other.classList.remove("is-selected");
              other.setAttribute("aria-pressed", "false");
            });
            b.classList.add("is-selected");
            b.setAttribute("aria-pressed", "true");
            activate(c);
          });
          btns.push(b);
          opts_.appendChild(b);
        });
        field.appendChild(opts_);
        body.appendChild(field);
        var shaftHelp = labelBased ? null : buildShaftHelp();
        if (shaftHelp) body.appendChild(shaftHelp);
      }

      // container the resolve step rebuilds on each (shaft) selection
      var dyn = el("div");
      body.appendChild(dyn);

      var main = el("div", "ocf__card-main");
      main.appendChild(media);
      main.appendChild(body);
      // Right column: what this cover type looks like fitted, so the shopper
      // can tell the types apart without reading the copy. The set contains
      // both covers, so it shows both drawings, captioned.
      // `opts.diagram` overrides the by-type drawing. Needed because the type key
      // is derived from the style name, and "Universal" resolves to the full-cover
      // drawing — right for the universal FULL cover, wrong for the universal
      // COWLING cover (MA 074), which sells a different product entirely. Same
      // mistake the shared "What is a Universal Cover?" video made on that card.
      var diagramParts = card.dataset.type === "set"
        ? [{ src: DIAGRAMS.full, caption: t("caption_full", "Full cover") }, { src: DIAGRAMS.vented, caption: t("caption_vented", "Vented cover") }]
        : [{ src: opts.diagram || DIAGRAMS[card.dataset.type], caption: "" }];
      diagramParts = diagramParts.filter(function (p) { return p.src; });
      var dia = null;
      if (diagramParts.length) {
        dia = el("div", "ocf__card-diagram");
        if (diagramParts.length > 1) dia.classList.add("ocf__card-diagram--pair");
        diagramParts.forEach(function (part) {
          var fig = el("div", "ocf__card-diagram-fig");
          var diaImg = new Image();
          diaImg.src = part.src;
          diaImg.loading = "lazy";
          diaImg.alt = (part.caption || friendlyStyle(opts.style || "")) + " shown fitted";
          fig.appendChild(diaImg);
          if (part.caption) fig.appendChild(el("span", "ocf__card-diagram-cap", part.caption));
          dia.appendChild(fig);
        });
        main.appendChild(dia);
      }
      // "What is a …?" explainers sit under the diagram they explain (the set
      // carries both covers' explainers); a card with no diagram keeps them in
      // the body as before.
      if (opts.info && opts.style) {
        var infoStyles = card.dataset.type === "set"
          ? ["Full Storage Cover", "Vented / Running Cover"]
          : [opts.style];
        infoStyles.forEach(function (s) {
          var w = buildInfo(s);
          if (dia) w.classList.add("ocf__diagram-info");
          (dia || body).appendChild(w);
        });
      }
      card.appendChild(main);

      function clearMedia() {
        var imgs = media.querySelectorAll("img.ocf__card-img"), k;
        for (k = imgs.length - 1; k >= 0; k--) { imgs[k].parentNode.removeChild(imgs[k]); }
      }
      function paintImage(img, alt) {
        clearMedia();
        if (img && img.url) {
          var im = new Image();
          im.loading = "lazy"; im.src = img.url; im.alt = alt || "";
          im.className = "ocf__card-img";
          media.insertBefore(im, media.firstChild);
        }
      }
      function enquireState(cover) {
        card.classList.remove("ocf__card--loading");
        card.classList.add("ocf__card--enquire");
        clearMedia();
        dyn.innerHTML = "";
        title.textContent = (opts.style || t("outboard_cover", "Outboard cover"));
        var s = sizeText(cover);
        dyn.appendChild(el("p", "ocf__size", fill(t("unavailable_sku", "Currently unavailable — SKU %s"), "sku", cover.sku) + (s ? " · " + s : "")));
        var actions = el("div", "ocf__actions");
        var cta = el("a", "ocf__cta ocf__cta--ghost", t("contact_us", "Contact us"));
        cta.setAttribute("href", enquireUrl);
        actions.appendChild(cta);
        dyn.appendChild(actions);
      }

      // resolve + render a single cover into the card
      function activate(cover) {
        if (opts.onActivate) opts.onActivate(cover);
        card.classList.remove("ocf__card--enquire");
        clearMedia();
        dyn.innerHTML = "";

        if (cover.unresolved || !cover.handle) { enquireState(cover); return; }

        card.classList.add("ocf__card--loading");
        var sizeEl = el("p", "ocf__size", isMulti ? "" : sizeText(cover));
        var colorsWrap = el("div", "ocf__colors");
        var price = el("p", "ocf__price", "");
        var sku = el("p", "ocf__sku", ""); sku.hidden = true;
        var actions = el("div", "ocf__actions");
        var view = el("a", "ocf__cta", t("view_cover", "View cover")); view.setAttribute("href", "#");
        actions.appendChild(view);
        dyn.appendChild(sizeEl);
        dyn.appendChild(colorsWrap);
        dyn.appendChild(price);
        dyn.appendChild(sku);
        dyn.appendChild(actions);

        fetchProduct(cover.handle).then(function (product) {
          card.classList.remove("ocf__card--loading");
          if (!product) { // network/API failure: degrade to a plain PDP link
            colorsWrap.remove(); price.remove(); sizeEl.remove();
            view.setAttribute("href", productUrl(cover.handle, null));
            return;
          }
          title.textContent = product.title || (opts.style || t("outboard_cover", "Outboard cover"));
          var variants = (product.variants && product.variants.nodes) || [];
          var matched = matchVariant(variants, cover.sku);
          if (!matched) { enquireState(cover); return; }

          var current = matched, addBtn = null;

          function paint(variant) {
            current = variant;
            paintImage(variant.image || product.featuredImage, title.textContent);
            sizeEl.textContent = isMulti ? "" : sizeText(cover);
            if (variant.availableForSale) {
              price.textContent = formatPrice(variant.price);
              price.className = "ocf__price";
              if (addBtn) { addBtn.disabled = false; addBtn.textContent = t("add_to_cart", "Add to cart"); }
            } else {
              price.textContent = t("unavailable", "Currently unavailable");
              price.className = "ocf__price ocf__price--soldout";
              if (addBtn) addBtn.disabled = true;
            }
            if (variant.sku) { sku.textContent = t("sku_label", "SKU") + ": " + variant.sku; sku.hidden = false; }
            else { sku.hidden = true; }
            view.setAttribute("href", productUrl(cover.handle, numericId(variant.id)));
          }

          // colour swatches (only when a colour option exists)
          var sibs = colorSiblings(variants, matched);
          if (sibs && sibs.length > 1) {
            colorsWrap.appendChild(el("span", "ocf__colors-label", t("colour", "Colour")));
            var rowc = el("div", "ocf__swatches");
            var swEls = [];
            for (var i = 0; i < sibs.length; i++) {
              (function (sib) {
                var b = el("button", "ocf__swatch");
                b.type = "button";
                b.setAttribute("aria-label", sib.name);
                b.title = sib.name;
                var dot = el("span", "ocf__swatch-dot");
                dot.style.background = colorHex(sib.name);
                b.appendChild(dot);
                b.appendChild(el("span", null, sib.name));
                b.addEventListener("click", function () {
                  for (var e2 = 0; e2 < swEls.length; e2++) { swEls[e2].classList.remove("is-selected"); }
                  b.classList.add("is-selected");
                  paint(sib.variant);
                });
                if (sib.variant.sku === matched.sku) b.classList.add("is-selected");
                swEls.push(b);
                rowc.appendChild(b);
              })(sibs[i]);
            }
            colorsWrap.appendChild(rowc);
          } else {
            colorsWrap.remove();
          }

          addBtn = el("button", "ocf__cta ocf__cta--ghost", t("add_to_cart", "Add to cart"));
          addBtn.type = "button";
          addBtn.addEventListener("click", function () {
            addBtn.disabled = true; addBtn.textContent = t("adding", "Adding…");
            fetch("/cart/add.js", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ id: parseInt(numericId(current.id), 10), quantity: 1 }),
            })
              .then(function (r) { return r.ok ? r.json() : Promise.reject(); })
              .then(function () { addBtn.textContent = t("added", "Added ✓"); refreshCart(); document.dispatchEvent(new CustomEvent("ocf:added")); setTimeout(function () { addBtn.disabled = false; }, 60); })
              .catch(function () { addBtn.textContent = t("add_to_cart", "Add to cart"); addBtn.disabled = false; });
          });
          actions.appendChild(addBtn);

          paint(matched);
        });
      }

      activate(firstCover);
      return card;
    }

    // ---------- universal cover card ----------
    // One component, two modes. Linked (opts.linked set): shows the
    // universal cover the data links to the custom Full cover's currently
    // selected SKU, following its shaft buttons via setLinked. Fallback:
    // shows this engine's universal range with buildCard's own shaft picker.
    // The Range line is a static label with one keyword, a select with more.
    function uniCoverFor(sku, shaft) {
      var p = (DATA.universalProducts || {})[sku] || {};
      var c = { style: "Universal", sku: sku, shaft: (shaft == null ? null : shaft) };
      if (p.handle) c.handle = p.handle; else c.unresolved = true;
      if (p.label && p.label !== sku) c.label = p.label;
      return c;
    }
    function buildUniversalCard(brand, hp, opts) {
      opts = opts || {};
      var ranges = ((DATA.universalRanges || {})[brand] || {})[String(hp)] || null;
      var keywords = ranges ? Object.keys(ranges) : [];
      var linked = opts.linked || null;      // {sku, shaft, keyword} | null
      var links = opts.links || [];          // every universal this engine links to
      if (!ranges && !linked && !links.length) return null;

      var wrap = el("div", "ocf__unicard");
      var userKeyword = null; // set once the shopper picks a range explicitly

      // universalRanges values are LISTS of {sku, years} — one shaft can carry
      // two covers separated only by model year (Suzuki 250 25": MA 075-8 for
      // 2003–c.2017, MA 075-9 for 2012).
      function entriesAt(map, shaftKey) {
        var v = map ? map[shaftKey] : null;
        if (!v) return [];
        return Object.prototype.toString.call(v) === "[object Array]" ? v : [{ sku: v, years: "" }];
      }
      function defaultKeyword() {
        if (linked) {
          for (var i = 0; i < keywords.length; i++) {
            var m = ranges[keywords[i]], s, j, es;
            for (s in m) {
              es = entriesAt(m, s);
              for (j = 0; j < es.length; j++) { if (es[j].sku === linked.sku) return keywords[i]; }
            }
          }
        }
        if (keywords.indexOf("Non-Specific Model") >= 0) return "Non-Specific Model";
        return keywords[0] || "Non-Specific Model";
      }
      function currentKeyword() { return userKeyword || defaultKeyword(); }

      // The wording shown against the Range control. Two vocabularies (250826):
      // reaching the universal option FROM a chosen motor shows that motor's
      // engine-specific keyword (E-TEC, V8 4.6L, MFS …) carried on the link;
      // entering via "Previous Generation Outboards" shows the general
      // Universal Range wording (Non-Specific Model, High Output (H.O.) …).
      function engineKeyword() {
        if (userKeyword) return null;
        if (linked && linked.keyword) return linked.keyword;
        for (var i = 0; i < links.length; i++) { if (links[i].keyword) return links[i].keyword; }
        return null;
      }

      // Range control — only when there's real range data to back it. Rebuilt
      // on every render (the engine-specific keyword can change when the
      // shopper switches shaft) and inserted INSIDE the card body, after the
      // title, so it sits with the other options rather than above the card.
      // "Non-Specific Model" is the option a shopper picks when they can't
      // identify their motor, so the explainer belongs with the Range control
      // — the moment they have to make that call.
      function withNonSpecificHelp(ctrl) {
        var offersNS = keywords.indexOf("Non-Specific Model") >= 0;
        if (!ctrl || !offersNS) return ctrl;
        var box = el("div", "ocf__unicard-rangewrap");
        box.appendChild(ctrl);
        box.appendChild(buildTextHelp(NON_SPECIFIC_HELP));
        return box;
      }
      function buildRangeControl() {
        // Linked from a specific motor: show that motor's wording, not the
        // general Universal Range list. Static — the shopper picked the engine.
        var ek = engineKeyword();
        if (ek) return el("p", "ocf__unicard-rangelabel", t("range", "Range") + ": " + ek);
        if (keywords.length > 1) {
          var box = el("div", "ocf__field ocf__unicard-range");
          var labId = "ocf-range-" + Math.round(performance.now() * 1000);
          var lab = el("label", "ocf__label", t("range", "Range"));
          lab.id = labId; lab.setAttribute("for", labId + "-sel");
          var sel = el("select", "ocf__select");
          sel.id = labId + "-sel";
          keywords.forEach(function (k) {
            var o = el("option", null, k); o.value = k; sel.appendChild(o);
          });
          sel.value = currentKeyword();
          sel.addEventListener("change", function () {
            userKeyword = sel.value;
            renderInner();
          });
          box.appendChild(lab);
          box.appendChild(sel);
          return withNonSpecificHelp(box);
        }
        if (keywords.length === 1) {
          return withNonSpecificHelp(el("p", "ocf__unicard-rangelabel", t("range", "Range") + ": " + keywords[0]));
        }
        return null;
      }

      var inner = el("div");
      wrap.appendChild(inner);

      // The selected keyword's FULL shaft->sku map renders as length buttons
      // (spec item E) — the linked entry preselects, and is added as an extra
      // option if the map doesn't already contain it.
      function coversForRender() {
        var covers = [], s, seen = {}, es, j, cov;

        // Shopper picked a motor: its own links ARE the universal options for
        // it. Sourcing from the generic HP range instead mixed in covers the
        // motor doesn't map to, giving two conflicting same-shaft buttons.
        // Switching Range (userKeyword) opts back into the generic ranges.
        if (links.length && !userKeyword) {
          for (j = 0; j < links.length; j++) {
            covers.push(uniCoverFor(links[j].sku, links[j].shaft));
          }
          covers.sort(function (a, b) { return (a.shaft || 0) - (b.shaft || 0); });
          return covers;
        }

        var kw = currentKeyword();
        var m = ranges && ranges[kw];
        if (m) {
          for (s in m) {
            es = entriesAt(m, s);
            for (j = 0; j < es.length; j++) {
              cov = uniCoverFor(es[j].sku, s === "" ? null : Number(s));
              // Carried so a shaft holding two year-variants can label them.
              if (es[j].years) cov.years = es[j].years;
              covers.push(cov);
              seen[es[j].sku + "|" + s] = true;
            }
          }
        }
        if (linked && !userKeyword) {
          var lk = linked.sku + "|" + (linked.shaft == null ? "" : String(linked.shaft));
          if (!seen[lk]) covers.push(uniCoverFor(linked.sku, linked.shaft));
        }
        covers.sort(function (a, b) { return (a.shaft || 0) - (b.shaft || 0); });
        // Same-shaft duplicates read as one ambiguous button. Prefer the model
        // year to tell them apart (that IS the difference — Suzuki 250 25" is
        // MA 075-8 for 2003–c.2017 and MA 075-9 for 2012); fall back to the SKU
        // when the rows carry no year.
        var byLabel = {}, dupLabel, dc;
        for (dc = 0; dc < covers.length; dc++) {
          dupLabel = shaftLabel(covers[dc].shaft);
          if (!dupLabel) continue;
          byLabel[dupLabel] = (byLabel[dupLabel] || 0) + 1;
        }
        for (dc = 0; dc < covers.length; dc++) {
          dupLabel = shaftLabel(covers[dc].shaft);
          if (!dupLabel || byLabel[dupLabel] < 2) continue;
          covers[dc].optionLabel = dupLabel + " · " + (covers[dc].years || covers[dc].sku);
        }
        return covers;
      }

      function renderInner() {
        inner.innerHTML = "";
        var covers = coversForRender();
        if (!covers.length) { wrap.hidden = true; return; }
        wrap.hidden = false;
        var cardEl = buildCard(covers, {
          style: "Universal", eyebrow: t("eyebrow_universal_full", "Universal Full Cover"), info: true,
          preselect: (linked && !userKeyword) ? linked : null,
        });
        // "Why choose a Universal …?" sits FIRST, above the "What is a
        // Universal Cover?" video — the shopper needs the case for the range
        // before the explanation of the product.
        var uniHost = cardEl.querySelector(".ocf__card-diagram") || cardEl.querySelector(".ocf__card-body");
        if (uniHost) {
          var whyHelp = buildTextHelp(UNIVERSAL_WHY_HELP);
          if (uniHost.classList.contains("ocf__card-diagram")) whyHelp.classList.add("ocf__diagram-info");
          var firstInfo = uniHost.querySelector(".ocf__videobtn");
          uniHost.insertBefore(whyHelp, firstInfo ? (firstInfo.closest(".ocf__texthelp") || firstInfo.parentNode) : null);
        }
        var cardBody = cardEl.querySelector(".ocf__card-body");
        var rangeControl = buildRangeControl();
        if (rangeControl && cardBody) {
          cardBody.insertBefore(rangeControl, cardBody.children[2] || null);
        }
        inner.appendChild(cardEl);
      }

      renderInner();
      return {
        elm: wrap,
        setLinked: function (link) {
          linked = link || null;
          userKeyword = null; // a shaft change re-syncs the range to the link
          renderInner();      // rebuilds the range control from the new link
        },
      };
    }

    // Universal cowling (drawstring) cover matched purely by HP range —
    // MA 074 family. Boundary HPs match two sizes; both render as options
    // labelled by their size.
    function buildCowlingUniversalCard(hp) {
      var entries = (DATA.universalCowling || []).filter(function (c) {
        return c.hpMin != null && hp >= c.hpMin && hp <= c.hpMax;
      });
      if (!entries.length) return null;
      var covers = entries.map(function (c) {
        var cov = uniCoverFor(c.sku, null);
        cov.optionLabel = String(c.label || c.sku).replace(/^Outboard Cover\s*/i, "");
        cov.label = c.label;
        return cov;
      });
      var wrap = el("div", "ocf__unicard ocf__unicard--cowling");
      // info:false — the shared "Universal" video explains the Full Storage
      // cover, which isn't what this card sells. Its own explainer replaces it.
      var cowlCard = buildCard(covers, {
        style: "Universal", eyebrow: t("eyebrow_universal_cowling", "Universal Cowling Cover"), info: false,
        // Show the cowling drawing, not the full-cover one the "Universal" type
        // would otherwise pick — this card sells a drawstring cowling cover.
        diagram: DIAGRAMS.cowling,
      });
      var cowlHost = cowlCard.querySelector(".ocf__card-diagram") || cowlCard.querySelector(".ocf__card-body");
      if (cowlHost) {
        var cowlHelp = buildTextHelp(UNIVERSAL_COWLING_HELP);
        if (cowlHost.classList.contains("ocf__card-diagram")) cowlHelp.classList.add("ocf__diagram-info");
        cowlHost.appendChild(cowlHelp);
      }
      wrap.appendChild(cowlCard);
      return wrap;
    }

    // ---------- render covers for a chosen engine ----------
    // Offer the brand's cover set when this engine's full-cover SKUs have
    // matching combo variants. Async: the card joins the results once the
    // store has confirmed the variants, landing in a fixed slot between the
    // Cowling card and the universal section — never advertised before the
    // store has confirmed a set can actually be bought.
    function appendComboCard(brand, engine, slot) {
      var handle = COMBO_HANDLES[brand];
      if (!handle) return;
      var fulls = (engine.covers || []).filter(function (c) {
        return c.style === "Full Storage Cover" && c.shaft != null && c.shaft !== "";
      });
      if (!fulls.length) return;
      var token_ = ++renderToken;
      fetchProduct(handle).then(function (product) {
        if (!product || token_ !== renderToken) return; // selection moved on
        var have = {};
        (((product.variants || {}).nodes) || []).forEach(function (v) {
          if (v.sku) have[String(v.sku).replace(/\s+/g, "").toUpperCase()] = true;
        });
        var combos = fulls
          .filter(function (c) { return have[comboSku(c.sku).toUpperCase()]; })
          .map(function (c) {
            return { style: COMBO_STYLE, sku: comboSku(c.sku), shaft: c.shaft, handle: handle };
          });
        if (!combos.length) return;
        // Lead with the set: it is the best-value option, so it belongs above
        // the individual covers rather than read as an afterthought.
        var comboCard = buildCard(combos, { style: COMBO_STYLE, eyebrow: t("eyebrow_combo", "Cover Set · Save on both"), info: true });
        // Sits with the two "What is a …?" explainers the set already carries,
        // answering the question they don't: why buy both together.
        var comboWhy = buildTextHelp(COMBO_HELP);
        var comboHost = comboCard.querySelector(".ocf__card-diagram") || comboCard.querySelector(".ocf__card-body");
        if (comboHost) {
          if (comboHost.classList.contains("ocf__card-diagram")) comboWhy.classList.add("ocf__diagram-info");
          comboHost.appendChild(comboWhy);
        }
        slot.appendChild(comboCard);
      });
    }

    function renderCovers(engine, hp, brand) {
      results.innerHTML = "";
      emptyNote.hidden = true;
      renderToken++; // invalidate any in-flight combo lookup from a prior pick

      // Unresolved custom covers have no product to sell: dropped entirely
      // — the universal section carries the engine.
      var resolved = (engine.covers || []).filter(function (c) {
        return c.handle && !c.unresolved;
      });

      // Slot 4: the Cover Set combo resolves async and lands here, keeping
      // the order Vented -> Full -> Cowling -> Combo -> Universal.
      var comboSlot = el("div");
      if (resolved.length && brand) appendComboCard(brand, engine, comboSlot);

      var uniCard = null, lastFullSku = null;
      function linkFor(sku) { return (DATA.universalLink || {})[sku] || null; }

      // A Full cover with no product is dropped above, so its onActivate never
      // fires and the universal card would fall back to the generic range —
      // losing the specific MA 075 the master data links to that motor. Seed
      // the link from the engine's FULL cover list, dropped ones included.
      (engine.covers || []).forEach(function (c) {
        if (c.style !== "Full Storage Cover") return;
        if (lastFullSku || (c.handle && !c.unresolved)) return;
        if (linkFor(c.sku)) lastFullSku = c.sku;
      });

      (DATA.styleOrder || STYLE_ORDER).forEach(function (style) {
        var group = resolved.filter(function (c) { return c.style === style; });
        if (!group.length) return;
        var isFull = style === "Full Storage Cover";
        var card = buildCard(group, {
          // eyebrow is the visible badge -> translated display name.
          // `style` stays the raw data key, which is what matching uses.
          style: style, eyebrow: friendlyStyle(style), info: true,
          onActivate: isFull ? function (cover) {
            lastFullSku = cover.sku;
            if (uniCard) uniCard.setLinked(linkFor(cover.sku));
          } : null,
        });
        results.appendChild(card);
      });

      results.appendChild(comboSlot);

      // Every universal cover this engine's Full covers link to, one per shaft.
      // When the shopper has picked a motor these ARE its universal options —
      // the generic HP range can disagree with them (Mercury 90: the motor
      // links 20" to MA 075-6, the range says MA 075-7) and merging the two
      // produced two conflicting "20-inch" buttons.
      var engineLinks = [];
      (engine.covers || []).forEach(function (c) {
        if (c.style !== "Full Storage Cover") return;
        var lk = linkFor(c.sku);
        if (!lk) return;
        for (var i = 0; i < engineLinks.length; i++) {
          if (engineLinks[i].sku === lk.sku && engineLinks[i].shaft === lk.shaft) return;
        }
        engineLinks.push(lk);
      });

      // Slot 5: the universal section — on EVERY engine.
      uniCard = buildUniversalCard(brand, hp, {
        linked: lastFullSku ? linkFor(lastFullSku) : null,
        links: engineLinks,
      });
      if (uniCard) results.appendChild(uniCard.elm);
      var cowlingCard = buildCowlingUniversalCard(hp);
      if (cowlingCard) results.appendChild(cowlingCard);
    }

    // ---------- engine confirm tiles ----------
    function yearRange(engine) {
      var lab = ROOT_LABELS[engine.root];
      if (lab && lab.years) return lab.years; // correction wins over the export
      if (engine.yearFrom == null) return "";
      return engine.yearTo ? (engine.yearFrom + "–" + engine.yearTo) : (engine.yearFrom + "+");
    }
    // Full-screen zoom so the HP number printed on the cowl is readable.
    function openEngineZoom(url, caption) {
      var overlay = el("div", "ocf__zoom");
      var big = el("img", "ocf__zoom-img");
      big.src = url; big.alt = caption;
      var close = el("button", "ocf__zoom-close", "×");
      close.type = "button"; close.setAttribute("aria-label", t("close", "Close"));
      overlay.appendChild(close);
      overlay.appendChild(big);
      overlay.appendChild(el("div", "ocf__zoom-caption", caption));
      function tearDown() {
        document.removeEventListener("keydown", onKey);
        if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
      }
      function onKey(e) { if (e.key === "Escape") tearDown(); }
      overlay.addEventListener("click", tearDown); // click anywhere closes
      document.addEventListener("keydown", onKey);
      document.body.appendChild(overlay);
    }

    function buildEngineTile(brand, hp, engine, noteOverride) {
      var tile = el("div", "ocf__engine-tile");
      var wrap = el("div", "ocf__engine-imgwrap");
      var img = el("img", "ocf__engine-img");
      img.alt = brand + " " + engine.root;
      wrap.classList.add("is-loading"); // skeleton shimmer until the photo decodes
      wrap.appendChild(img);
      // The zoom circle is a real button: it's the ONLY thing that zooms, so a
      // click anywhere else on the card falls through to advance to the covers.
      var hint = el("button", "ocf__engine-zoomhint");
      hint.type = "button";
      hint.setAttribute("aria-label", t("zoom_in", "Zoom in"));
      hint.innerHTML =
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/><path d="M11 8v6"/><path d="M8 11h6"/></svg>';
      wrap.appendChild(hint);
      tile.appendChild(wrap);
      var hpLine = el("div", "ocf__engine-hp", engineTitle(engine, hp));
      tile.appendChild(hpLine);
      var note = engineNote(engine) || noteOverride || "";
      if (note) tile.appendChild(el("div", "ocf__engine-note", note));
      var yr = yearRange(engine);
      var yearLine = el("div", "ocf__engine-year", yr);
      // always present so the previous-generation fallback can fill it in
      tile.appendChild(yearLine);

      // Reveal the image only once it's fully decoded: the skeleton shimmers
      // until then, then fades out as the crisp photo fades in (no streaming).
      function paint(src) {
        var pre = new Image();
        function show() {
          img.src = src;
          wrap.classList.remove("is-loading");
          wrap.classList.add("is-loaded");
        }
        pre.onload = show;
        pre.onerror = show;
        pre.src = src;
      }

      var handle = slug(brand + "-" + engine.root + "-" + hp);
      try {
        fetchEngineImage(handle).then(function (url) {
          if (url) {
            paint(url);
            // real photo in — reveal the zoom circle (only it zooms)
            wrap.classList.add("is-zoomable");
            var caption = brand + " " + hp + " HP" + (yr ? " · " + yr : "");
            hint.addEventListener("click", function (e) {
              e.stopPropagation(); // zoom only — don't also advance to the covers
              openEngineZoom(url, caption);
            });
          } else if (olderImg) {
            // The composite drawing is the stand-in when there's no photo, but
            // it must not rename the motor. It used to relabel ANY photo-less
            // engine "Previous Generation Outboards / 1985 onwards" — which put
            // that caption on current models (Mercury M000 is 2018+, Suzuki
            // S00A is 2025+) and made them indistinguishable from the generic
            // previous-generation entry. Only the synthetic tile gets that name.
            paint(olderImg);
            tile.classList.add("is-older");
            if (engine.root === PSEUDO_ROOT) {
              hpLine.textContent = t("previous_generation", "Previous Generation Outboards");
              yearLine.textContent = t("year_1985_onwards", "1985 onwards");
            } else {
              // A real motor awaiting a photo: keep its own HP and years so the
              // shopper can still tell the tiles apart.
              tile.classList.add("is-awaiting-photo");
            }
          } else {
            // Nothing to show — stop the skeleton, leave a plain neutral frame.
            wrap.classList.remove("is-loading");
          }
        }).catch(function () { wrap.classList.remove("is-loading"); });
      } catch (e) { /* never let an image lookup break the finder */ }
      return tile;
    }

    // On a phone the covers land below the fold, so picking an engine looks
    // like nothing happened. Bring the results into view and flash them once
    // so the choice reads as "here is what you selected".
    // The theme's header sticks; scrolling to a raw offset would tuck the
    // target underneath it.
    function stickyHeaderOffset() {
      var h = document.querySelector(".shopify-section-main-header, .shopify-section-header, header");
      if (!h) return 0;
      var pos = window.getComputedStyle(h).position;
      if (pos !== "fixed" && pos !== "sticky") return 0;
      return h.getBoundingClientRect().height || 0;
    }

    function revealResults() {
      if (!results.firstChild) return;
      var reduced = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      results.classList.remove("ocf__results--revealed");
      // restart the animation on repeat selections
      void results.offsetWidth;
      results.classList.add("ocf__results--revealed");
      // Scroll on every viewport, not just phones: on desktop the heading,
      // intro and two selects can already fill the screen, so the covers land
      // below the fold there too. Aim at the "your engine" summary, which is
      // the line that confirms the click landed.
      var target = chosenBar || results;
      var top = target.getBoundingClientRect().top + (window.pageYOffset || 0) - stickyHeaderOffset() - 12;
      try {
        window.scrollTo({ top: Math.max(0, top), behavior: reduced ? "auto" : "smooth" });
      } catch (e) {
        window.scrollTo(0, Math.max(0, top)); // older Safari: no options object
      }
    }

    // The confirm tiles and the covers occupy the same slot: choosing an engine
    // swaps the row out for the covers, so the answer appears where the shopper
    // was already looking instead of below the fold. A summary bar keeps the
    // choice visible and reversible.
    function showChosenEngine(brand, hp, engine, restore) {
      if (chosenBar) chosenBar.parentNode.removeChild(chosenBar);
      chosenBar = el("div", "ocf__chosen");
      var text = el("div", "ocf__chosen-text");
      text.appendChild(el("span", "ocf__chosen-label", t("your_engine", "Your engine")));
      var note = engineNote(engine);
      text.appendChild(el("strong", "ocf__chosen-name",
        brand + " " + engineTitle(engine, hp) + (note ? " · " + note : "")));
      var back = el("button", "ocf__chosen-back");
      back.type = "button";
      back.innerHTML = '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 18l-6-6 6-6"/></svg>';
      back.appendChild(el("span", null, t("choose_different_engine", "Choose a different engine")));
      back.addEventListener("click", restore);
      chosenBar.appendChild(text);
      chosenBar.appendChild(back);
      engineBlock.insertBefore(chosenBar, tilesWrap);
    }

    function enterCoversView(brand, hp, engine) {
      prompt_.hidden = true;
      tilesWrap.hidden = true;
      showChosenEngine(brand, hp, engine, function () {
        // back to the row, with the previous pick still highlighted
        resetResults();
        if (chosenBar) { chosenBar.parentNode.removeChild(chosenBar); chosenBar = null; }
        prompt_.hidden = false;
        tilesWrap.hidden = false;
        engineBlock.scrollIntoView({ block: "nearest" });
      });
      renderCovers(engine, hp, brand);
      revealResults();
    }

    function renderEngineTiles(brand, hp, engines) {
      tilesWrap.innerHTML = "";
      tilesWrap.hidden = false;
      prompt_.hidden = false;
      if (chosenBar) { chosenBar.parentNode.removeChild(chosenBar); chosenBar = null; }
      var single = engines.length === 1;
      tilesWrap.classList.toggle("ocf__engine-tiles--single", single);
      // One batched, resized image query for the whole selection before the
      // tiles ask for their photos — they then resolve together from cache.
      fetchEngineImages(engines.map(function (e) { return slug(brand + "-" + e.root + "-" + hp); }));
      // Two engines can share an HP and a year range (Yamaha 150: Y10 and Y15
      // are both 2015-2022) and would render as identical tiles with no way to
      // choose between them — yet they carry different covers. Where that
      // happens, fall back to the engine descriptor OS supplies on the universal
      // link ("4 CYL 2.8L" vs "4 CYL 2.7L") to tell them apart.
      var labelCount = {};
      engines.forEach(function (e) {
        var k = engineTitle(e, hp) + "|" + yearRange(e);
        labelCount[k] = (labelCount[k] || 0) + 1;
      });
      function linkKeyword(e) {
        var links = DATA.universalLink || {}, i, c, lk;
        for (i = 0; i < (e.covers || []).length; i++) {
          c = e.covers[i];
          lk = links[c.sku];
          if (lk && lk.keyword) return lk.keyword;
        }
        return "";
      }

      engines.forEach(function (engine) {
        var dupKey = engineTitle(engine, hp) + "|" + yearRange(engine);
        var tile = buildEngineTile(brand, hp, engine,
          labelCount[dupKey] > 1 ? linkKeyword(engine) : "");
        // Every tile is clickable — including a lone one. Single-engine
        // selections auto-advance below, but "Choose a different engine"
        // reveals the tile row again; without a listener the lone tile is a
        // dead end the shopper can't click back into.
        tile.addEventListener("click", function () {
          var sibs = tilesWrap.querySelectorAll(".ocf__engine-tile"), k;
          for (k = 0; k < sibs.length; k++) { sibs[k].classList.remove("is-selected"); }
          tile.classList.add("is-selected");
          enterCoversView(brand, hp, engine);
        });
        tilesWrap.appendChild(tile);
      });
      // Old-engine entry point: the universal fitment rows cover engines far
      // older than the custom list (c. 1990-2005). When this selection has
      // universal data and every real tile resolved its own photo, append a
      // synthetic "Previous Generation Outboards" tile that leads to the
      // universal-only results. (A photo-less real engine already renders as
      // the previous-generation composite — appending would duplicate it.)
      var wrapGen = (tilesWrap.__gen = (tilesWrap.__gen || 0) + 1);
      var hasRanges = !!(((DATA.universalRanges || {})[brand] || {})[String(hp)]);
      if (hasRanges && olderImg) {
        Promise.all(engines.map(function (e) {
          return fetchEngineImage(slug(brand + "-" + e.root + "-" + hp));
        })).then(function (urls) {
          if (tilesWrap.__gen !== wrapGen) return; // selection moved on
          // This used to bail when any engine lacked a photo, on the assumption
          // that a photo-less engine WAS the previous-generation option. It
          // isn't — it's a specific motor wearing a placeholder — and skipping
          // the tile left the Universal Range options unreachable for that HP
          // (Mercury 175: VERADO and OPTIMAX / PRO XS had no path at all).
          var pseudo = { root: PSEUDO_ROOT, hp: [hp], covers: [], yearFrom: null, yearTo: null };
          var tile = buildEngineTile(brand, hp, pseudo);
          tile.addEventListener("click", function () {
            var sibs = tilesWrap.querySelectorAll(".ocf__engine-tile"), k;
            for (k = 0; k < sibs.length; k++) { sibs[k].classList.remove("is-selected"); }
            tile.classList.add("is-selected");
            enterCoversView(brand, hp, pseudo);
          });
          tilesWrap.appendChild(tile);
          tilesWrap.classList.remove("ocf__engine-tiles--single");
        }).catch(function () { /* photo lookups never break the finder */ });
      }

      // The confirm step always shows — even a lone tile: seeing the engine
      // photo IS the confirmation, so the shopper clicks it to proceed.
    }

    // Placeholder <option> for the two selects. Built with el() rather than
    // innerHTML so a translated string is never interpolated into markup.
    function placeholderOption(text) {
      var o = el("option", null, text);
      o.value = "";
      return o;
    }

    // ---------- populate controls ----------
    function populateBrands() {
      brandSel.innerHTML = "";
      brandSel.appendChild(placeholderOption(t("select_brand", "Select brand…")));
      (DATA.brands || []).forEach(function (b) {
        var o = el("option", null, b); o.value = b; brandSel.appendChild(o);
      });
    }
    function populateHp(brand) {
      hpSel.innerHTML = "";
      hpSel.appendChild(placeholderOption(t("select_hp", "Select HP…")));
      var set = {};
      (DATA.engines[brand] || []).forEach(function (e) {
        (e.hp || []).forEach(function (h) { set[h] = h; });
      });
      // Some HPs are served ONLY by a universal range — no fitted cover exists,
      // so no engine carries them. They were absent from this dropdown entirely,
      // leaving those motors unselectable (Evinrude 4/8/100/140, Mercury 125,
      // Tohatsu 2.5/18/70/120/150/200/225/250, Yamaha 80).
      var uniHp = (DATA.universalRanges || {})[brand] || {};
      Object.keys(uniHp).forEach(function (k) {
        var h = parseFloat(k);
        if (!isNaN(h)) set[h] = h;
      });
      var vals = Object.keys(set).map(function (k) { return set[k]; }).sort(function (a, b) { return a - b; });
      vals.forEach(function (h) {
        var o = el("option", null, h + " HP"); o.value = String(h); hpSel.appendChild(o);
      });
      hpSel.disabled = vals.length === 0;
    }

    function resetResults() { results.innerHTML = ""; emptyNote.hidden = true; }
    function hideEngine() {
      engineBlock.hidden = true;
      tilesWrap.innerHTML = "";
      tilesWrap.hidden = false;
      tilesWrap.classList.remove("ocf__engine-tiles--single");
      if (prompt_) prompt_.hidden = false;
      if (chosenBar) { chosenBar.parentNode.removeChild(chosenBar); chosenBar = null; }
    }

    // ---------- wire up ----------
    brandSel.addEventListener("change", function () {
      resetResults(); hideEngine();
      hpSel.innerHTML = "";
      hpSel.appendChild(placeholderOption(t("select_hp", "Select HP…")));
      hpSel.disabled = true;
      if (!brandSel.value || !DATA) return;
      populateHp(brandSel.value);
    });

    hpSel.addEventListener("change", function () {
      resetResults();
      var brand = brandSel.value;
      var hp = parseFloat(hpSel.value);
      if (!brand || !hpSel.value || isNaN(hp) || !DATA) { hideEngine(); return; }
      var engines = (DATA.engines[brand] || []).filter(function (e) {
        return (e.hp || []).indexOf(hp) >= 0;
      });
      // No fitted cover for this HP is not a dead end — a universal range may
      // still serve it. renderEngineTiles appends the "Previous Generation
      // Outboards" tile when ranges exist, so an empty engine list still gives
      // the shopper somewhere to go. Only truly empty selections show the note.
      var hasUni = !!(((DATA.universalRanges || {})[brand] || {})[String(hp)]);
      if (!engines.length && !hasUni) { hideEngine(); emptyNote.hidden = false; return; }
      engineBlock.hidden = false;
      renderEngineTiles(brand, hp, engines);
    });

    // ---------- load data ----------
    if (!dataSrc) return;
    fetch(dataSrc)
      .then(function (r) { return r.json(); })
      .then(function (json) { DATA = json; populateBrands(); })
      .catch(function () {
        emptyNote.hidden = false;
        emptyNote.textContent = t("load_error", "Sorry — the outboard cover finder could not load. Please refresh or contact us.");
      });
  }

  function boot() {
    document.querySelectorAll(".ocf").forEach(init);
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
